import { useEffect, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
    FormProvider,
    useFieldArray,
    useForm,
    useFormContext,
} from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { z } from 'zod/v3'
import { Plus, Save, Trash2 } from 'lucide-react'

import type { LotOption } from '@/components/lots/lot-items-field'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { PageBackButton } from '@/components/common/PageBackButton'
import { StickyFormFooter } from '@/components/common/StickyFormFooter'
import { Button } from '@/components/ui/button'
import { PackagingMaterialPicker } from '@/components/packaging/packaging-material-picker'
import { FieldError } from '@/components/ui/field'
import { PositionPicker } from '@/components/warehouse-positions/position-picker'
import { Input } from '@/components/ui/input'
import { QuantityInput } from '@/components/ui/quantity-input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { useCreatePackagingInNote } from '@/hooks/use-packaging-lot-query'
// useWarehouses removed — BE auto-fills default (P9).
import { LotPicker } from '@/components/lots/lot-picker'
import { usePackagingLotSource } from '@/components/lots/lot-picker-source'
import { m } from '@/lib/paraglide/messages'

const itemSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('new'),
        new_lot_code: z.string().optional(),
        new_lot_material_id: z
            .string({ required_error: m.form_err_pick_material() })
            .uuid(m.form_err_pick_material()),
        new_lot_warehouse_id: z.string().uuid().optional(),
        new_lot_position_id: z
            .string({ required_error: m.warehouse_position_required() })
            .uuid({ message: m.warehouse_position_required() }),
        new_lot_supplier_name: z.string().optional(),
        quantity: z.coerce
            .number({
                required_error: m.form_err_quantity_required(),
                invalid_type_error: m.form_err_number_invalid(),
            })
            .positive(m.form_err_quantity_positive()),
    }),
    z.object({
        kind: z.literal('existing'),
        packaging_lot_id: z
            .string({ required_error: m.form_err_pick_existing_lot() })
            .uuid(m.form_err_pick_existing_lot()),
        quantity: z.coerce
            .number({
                required_error: m.form_err_quantity_required(),
                invalid_type_error: m.form_err_number_invalid(),
            })
            .positive(m.form_err_quantity_positive()),
    }),
])

const schema = z.object({
    reason: z.enum([
        'from_purchase',
        'adjustment',
        'return_from_packaging_process',
    ]),
    notes: z.string().max(2000, m.form_err_notes_too_long()).optional(),
    items: z.array(itemSchema).min(1, m.form_err_items_min_one()),
})

type FormData = z.infer<typeof schema>

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/packaging-warehouse/stock-in-notes/new',
)({
    component: NewPNKPage,
})

function NewPNKPage() {
    const navigate = useNavigate()
    const create = useCreatePackagingInNote()

    const handleSubmit = async (
        value: PackagingInSubmit,
        workflow: ReturnType<typeof useWorkflowTracking>,
    ) => {
        try {
            const res = await workflow.run(
                () => create.mutateAsync(value as never),
                (result) => result.data?.id,
            )
            const noteId = res.data?.id
            if (noteId) {
                void navigate({
                    to: '/packaging-warehouse/stock-in-notes/$id',
                    params: { id: noteId },
                })
            }
        } catch (err) {
            toast.error(
                err instanceof Error ? err.message : m.sl_new_error_create(),
            )
        }
    }

    return (
        <PackagingLotStockInForm
            isPending={create.isPending}
            onSubmit={handleSubmit}
            onCancel={() =>
                navigate({
                    to: '/packaging-warehouse/stock-in-notes',
                    search: { page: 1 },
                })
            }
        />
    )
}

type PackagingInSubmit = {
    reason: FormData['reason']
    notes?: string
    items: unknown[]
}

type LotOpt = LotOption

export function PackagingLotStockInForm({
    mode = 'create',
    initialValues,
    title,
    submitLabel,
    isPending,
    onSubmit,
    onCancel,
    seedLotOptions = [],
}: {
    mode?: 'create' | 'edit'
    initialValues?: FormData
    title?: string
    submitLabel?: string
    isPending: boolean
    onSubmit: (
        value: PackagingInSubmit,
        workflow: ReturnType<typeof useWorkflowTracking>,
    ) => void
    onCancel: () => void
    // Lô của item hiện có khi sửa. Lô lot-mode do PNK tạo đang 'pending' nên query
    // (chỉ trả lô active) lọc mất → seed để ô "Chọn lô" hiển thị đúng nhãn.
    seedLotOptions?: LotOpt[]
}) {
    const workflow = useWorkflowTracking('packaging_stock_in', {
        operation: mode === 'edit' ? 'update' : 'create',
    })
    const isEdit = mode === 'edit'
    const form = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues: initialValues ?? {
            reason: 'from_purchase',
            notes: '',
            items: [
                {
                    kind: 'new' as const,
                    new_lot_material_id: '',
                    new_lot_position_id: '',
                    quantity: 0,
                },
            ],
        },
    })

    useEffect(() => {
        const subscription = form.watch((_values, { name }) => {
            if (!name) return
            workflow.interact(name.startsWith('items') ? 'items' : 'details')
            if (name.endsWith('_lot_id') || name === 'reason')
                workflow.step('selection_changed')
        })
        return () => subscription.unsubscribe()
    }, [form, workflow])

    const submit = form.handleSubmit(
        (data) => {
            const items = data.items.map((it) =>
                it.kind === 'new'
                    ? {
                          new_lot_material_id: it.new_lot_material_id,
                          new_lot_position_id: it.new_lot_position_id,
                          new_lot_code: it.new_lot_code || undefined,
                          new_lot_supplier_name:
                              it.new_lot_supplier_name || undefined,
                          quantity: Number(it.quantity),
                      }
                    : {
                          packaging_lot_id: it.packaging_lot_id,
                          quantity: Number(it.quantity),
                      },
            )
            onSubmit(
                { reason: data.reason, notes: data.notes, items },
                workflow,
            )
        },
        () => workflow.step('validation_failed'),
    )

    return (
        <div className="space-y-4 page-pad pb-24 md:pb-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <PageBackButton onBack={onCancel} />
                    <h1 className="text-xl font-semibold">
                        {title ?? m.pkl_new_pnk_title()}
                    </h1>
                </div>
            </div>

            <FormProvider {...form}>
                <form onSubmit={submit} className="space-y-6 max-w-5xl">
                    <div className="grid gap-4 md:grid-cols-2 rounded-md border bg-card p-4">
                        <div>
                            <Label>{m.sl_new_reason_in()}</Label>
                            <Select
                                value={form.watch('reason')}
                                disabled={isEdit}
                                onValueChange={(v) =>
                                    form.setValue(
                                        'reason',
                                        v as FormData['reason'],
                                    )
                                }
                            >
                                <SelectTrigger className="w-full">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="from_purchase">
                                        {m.pkl_in_reason_from_purchase()}
                                    </SelectItem>
                                    <SelectItem value="return_from_packaging_process">
                                        {m.pkl_in_reason_return_from_packaging_process()}
                                    </SelectItem>
                                    <SelectItem value="adjustment">
                                        {m.pkl_in_reason_adjustment()}
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>{m.sl_new_notes()}</Label>
                            <Input
                                {...form.register('notes')}
                                placeholder={m.sl_new_notes_placeholder()}
                            />
                        </div>
                    </div>

                    <PackagingLotItemsField seedLotOptions={seedLotOptions} />

                    {form.formState.errors.items?.message && (
                        <p className="text-sm text-destructive">
                            {form.formState.errors.items.message}
                        </p>
                    )}

                    <StickyFormFooter>
                        <Button
                            type="button"
                            variant="outline"
                            className="h-11 flex-1 md:h-10 md:flex-none"
                            onClick={onCancel}
                        >
                            {m.sl_new_cancel()}
                        </Button>
                        <Button
                            type="submit"
                            className="h-11 flex-1 md:h-10 md:flex-none"
                            disabled={isPending}
                        >
                            <Save className="size-4 mr-1" />
                            {submitLabel ??
                                (isPending
                                    ? m.sl_new_submitting()
                                    : m.sl_new_submit_pnk())}
                        </Button>
                    </StickyFormFooter>
                </form>
            </FormProvider>
        </div>
    )
}

function PackagingLotItemsField({
    seedLotOptions = [],
}: {
    seedLotOptions?: LotOpt[]
}) {
    const { control, register, watch, setValue } = useFormContext<FormData>()
    const { fields, append, remove, update } = useFieldArray({
        control,
        name: 'items',
    })

    // Hoàn vật tư thừa từ đóng gói = trả về ĐÚNG lô đã xuất → cấm tạo lô mới.
    // Nếu không, hệ thống đẻ ra lô vật tư mới: tổng tồn vẫn đúng nhưng lô bị
    // phân mảnh và mất liên kết với lô nguồn.
    const reason = watch('reason')
    const allowNewLot = reason !== 'return_from_packaging_process'
    const items = watch('items')

    // Khi allowNewLot tắt (user đổi lý do sang "hoàn"), ép mọi row đang là 'new'
    // về 'existing'. Nếu không, row cũ kẹt ở 'new' TRONG KHI nút chuyển đã bị ẩn
    // — user không còn đường sửa, submit thì BE từ chối → kẹt cứng.
    useEffect(() => {
        if (allowNewLot) return
        ;(items ?? []).forEach((row, idx) => {
            if (row?.kind !== 'new') return
            update(idx, {
                kind: 'existing' as const,
                packaging_lot_id: '',
                quantity: row.quantity ?? 0,
            })
        })
    }, [allowNewLot, items, update])
    // Ô "lô có sẵn": tìm server + cuộn tải thêm (nguồn lô dùng chung). Seed =
    // lô của phiếu đang sửa (kể cả lô pending không có trong danh sách).
    const lotSource = usePackagingLotSource({ seed: seedLotOptions })

    // Map material_id → tracking_mode để form ẩn field lô khi vật tư qty-mode.
    // Tự tích luỹ từ chính lượt chọn thay vì dựng từ một danh sách tải sẵn:
    // ô chọn vật tư giờ phân trang nên không còn "cả danh sách" để tra.
    //
    // Đủ dữ liệu vì map CHỈ được đọc cho dòng `kind === 'new'`, mà dòng 'new'
    // chỉ sinh ra từ nút thêm (material rỗng) rồi người dùng chọn — không có
    // đường nào seed sẵn một dòng 'new' kèm material. Lưu ý form này còn dùng
    // cho màn SỬA (`$id_.edit.tsx`), nhưng ở đó mọi dòng seed là 'existing'.
    // Nếu sau này edit mode seed dòng 'new' có material, phải nạp mode cho
    // chúng ở đây, nếu không ô lô sẽ hiện nhầm cho vật tư qty-mode và BE trả
    // 400 ErrQtyModeItemHasLotFields.
    const [materialModeById, setMaterialModeById] = useState<
        Map<string, 'lot' | 'quantity'>
    >(new Map())
    const rememberMaterialMode = (id: string, mode: 'lot' | 'quantity') =>
        setMaterialModeById((prev) => new Map(prev).set(id, mode))
    // Đơn vị vật tư (cái, hộp, m…) theo material_id để hiển thị hậu tố ở ô KL của
    // lô mới. Tích luỹ từ chính lượt chọn (picker phân trang, không tải sẵn cả list).
    const [materialUnitById, setMaterialUnitById] = useState<
        Map<string, string>
    >(new Map())
    const rememberMaterialUnit = (id: string, unit: string) =>
        setMaterialUnitById((prev) => new Map(prev).set(id, unit))
    const addRow = () => {
        if (!allowNewLot) {
            append({
                kind: 'existing' as const,
                packaging_lot_id: '',
                quantity: 0,
            })
            return
        }
        append({
            kind: 'new' as const,
            new_lot_material_id: '',
            new_lot_position_id: '',
            quantity: 0,
        })
    }

    // Nút "Thêm lô" đặt DƯỚI danh sách item — thêm rồi sửa ngay tại chỗ, không
    // phải kéo lên đầu form mỗi lần muốn thêm dòng.
    const actionBar = (
        <Button type="button" size="sm" variant="outline" onClick={addRow}>
            <Plus className="size-4 mr-1" /> {m.lot_items_add()}
        </Button>
    )

    return (
        <div className="space-y-3">
            <Label>{m.lot_items_title()}</Label>

            {fields.length === 0 && (
                <div className="rounded-md border border-dashed bg-muted/30 py-6 text-center text-sm text-muted-foreground">
                    {m.lot_items_empty()}
                </div>
            )}

            {fields.map((field, idx) => {
                const kind = watch(`items.${idx}.kind`)
                const materialID = watch(`items.${idx}.new_lot_material_id`)
                const isQtyMode =
                    kind === 'new' &&
                    !!materialID &&
                    materialModeById.get(materialID) === 'quantity'
                return (
                    <div
                        key={field.id}
                        className="grid gap-3 rounded-md border bg-card p-3"
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-muted-foreground">
                                {m.lot_items_row({ n: idx + 1 })}
                            </span>
                            <div className="flex items-center gap-2">
                                <div
                                    className={`flex rounded-md border p-0.5 text-xs ${allowNewLot ? '' : 'hidden'}`}
                                >
                                    <button
                                        type="button"
                                        className={`px-2 py-1 rounded ${kind === 'new' ? 'bg-primary text-primary-foreground' : ''}`}
                                        onClick={() => {
                                            update(idx, {
                                                kind: 'new' as const,
                                                new_lot_material_id: '',
                                                new_lot_position_id: '',
                                                quantity: 0,
                                            })
                                        }}
                                    >
                                        {m.lot_items_new_lot()}
                                    </button>
                                    <button
                                        type="button"
                                        className={`px-2 py-1 rounded ${kind === 'existing' ? 'bg-primary text-primary-foreground' : ''} ${isQtyMode ? 'opacity-40 cursor-not-allowed' : ''}`}
                                        disabled={isQtyMode}
                                        onClick={() => {
                                            if (isQtyMode) return
                                            update(idx, {
                                                kind: 'existing' as const,
                                                packaging_lot_id: '',
                                                quantity: 0,
                                            })
                                        }}
                                        title={
                                            isQtyMode
                                                ? m.packaging_pnk_qty_mode_no_lot_tooltip()
                                                : undefined
                                        }
                                    >
                                        {m.lot_items_existing_lot()}
                                    </button>
                                </div>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => remove(idx)}
                                >
                                    <Trash2 className="size-4 text-destructive" />
                                </Button>
                            </div>
                        </div>

                        {kind === 'new' && (
                            <div className="grid gap-3 md:grid-cols-3">
                                <div>
                                    <Label className="text-xs">
                                        {m.pkl_new_lot_material_label()}
                                    </Label>
                                    <PackagingMaterialPicker
                                        value={watch(
                                            `items.${idx}.new_lot_material_id`,
                                        )}
                                        onPick={(mt) => {
                                            setValue(
                                                `items.${idx}.new_lot_material_id`,
                                                mt.id,
                                            )
                                            rememberMaterialMode(
                                                mt.id,
                                                mt.tracking_mode,
                                            )
                                            rememberMaterialUnit(mt.id, mt.unit)
                                            // Vật tư đổi sang qty-mode: xoá các
                                            // trường lô đã điền (nếu có) để tránh
                                            // gửi rác lên BE — BE sẽ 400 nếu
                                            // ErrQtyModeItemHasLotFields.
                                            // Đọc thẳng từ vật tư vừa chọn thay
                                            // vì tra map tải sẵn: picker giờ
                                            // phân trang nên map không còn đủ.
                                            if (
                                                mt.tracking_mode === 'quantity'
                                            ) {
                                                setValue(
                                                    `items.${idx}.new_lot_code`,
                                                    '',
                                                )
                                            }
                                        }}
                                        placeholder={m.pkl_new_lot_material_label()}
                                        activeOnly={false}
                                    />
                                    <FieldError
                                        name={`items.${idx}.new_lot_material_id`}
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">
                                        {m.lot_position_label()}
                                    </Label>
                                    <PositionPicker
                                        lotType="packaging"
                                        value={watch(
                                            `items.${idx}.new_lot_position_id`,
                                        )}
                                        onChange={(v) =>
                                            setValue(
                                                `items.${idx}.new_lot_position_id`,
                                                v,
                                            )
                                        }
                                    />
                                    <FieldError
                                        name={`items.${idx}.new_lot_position_id`}
                                    />
                                </div>
                                {!isQtyMode && (
                                    <div>
                                        <Label className="text-xs">
                                            {m.pkl_new_lot_code_label()}
                                        </Label>
                                        <Input
                                            {...register(
                                                `items.${idx}.new_lot_code`,
                                            )}
                                            placeholder="VT-LOT-..."
                                        />
                                        <FieldError
                                            name={`items.${idx}.new_lot_code`}
                                        />
                                    </div>
                                )}
                                <div>
                                    <Label className="text-xs">
                                        {m.pkl_new_lot_supplier_label()}
                                    </Label>
                                    <Input
                                        {...register(
                                            `items.${idx}.new_lot_supplier_name`,
                                        )}
                                        placeholder="VD: Cty ABC"
                                    />
                                    <FieldError
                                        name={`items.${idx}.new_lot_supplier_name`}
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">
                                        {m.pkl_new_quantity_label()}
                                    </Label>
                                    <QuantityInput
                                        unit={
                                            materialUnitById.get(
                                                watch(
                                                    `items.${idx}.new_lot_material_id`,
                                                ),
                                            ) || undefined
                                        }
                                        type="number"
                                        step="0.001"
                                        min={0}
                                        {...register(`items.${idx}.quantity`, {
                                            valueAsNumber: true,
                                        })}
                                    />
                                    <FieldError
                                        name={`items.${idx}.quantity`}
                                    />
                                </div>
                            </div>
                        )}

                        {kind === 'existing' && (
                            <div className="grid gap-3 md:grid-cols-2">
                                <div>
                                    <Label className="text-xs">
                                        {m.lot_items_select_lot()}
                                    </Label>
                                    <LotPicker
                                        source={lotSource}
                                        value={watch(
                                            `items.${idx}.packaging_lot_id`,
                                        )}
                                        onChange={(v) =>
                                            setValue(
                                                `items.${idx}.packaging_lot_id`,
                                                v,
                                            )
                                        }
                                    />
                                    <FieldError
                                        name={`items.${idx}.packaging_lot_id`}
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">
                                        {m.pkl_new_quantity_label()}
                                    </Label>
                                    <QuantityInput
                                        unit={
                                            lotSource.getOption(
                                                watch(
                                                    `items.${idx}.packaging_lot_id`,
                                                ),
                                            )?.unit || undefined
                                        }
                                        type="number"
                                        step="0.001"
                                        min={0}
                                        {...register(`items.${idx}.quantity`, {
                                            valueAsNumber: true,
                                        })}
                                    />
                                    <FieldError
                                        name={`items.${idx}.quantity`}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )
            })}

            {actionBar}
        </div>
    )
}
